import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Upload, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const QUANTITIES = ["50–100 pcs", "100–500 pcs", "500–1,000 pcs", "1,000+ pcs"];

const EMPTY = { name: "", company: "", phone: "", email: "", product_interest: "", quantity: "", message: "" };

export default function EnquiryForm({ products, contact, productInterest }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (productInterest) setForm((f) => ({ ...f, product_interest: productInterest }));
  }, [productInterest]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large — maximum 10MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setAttachment({ path: data.path, name: f.name });
      toast.success("Design attached to your enquiry");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed — try a JPG, PNG, WEBP or PDF under 10MB");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", {
        ...form,
        attachment_path: attachment?.path || "",
        attachment_name: attachment?.name || "",
      });
      toast.success("Enquiry sent! We'll call you back within one working day.");
      setForm(EMPTY);
      setAttachment(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" className="max-w-7xl mx-auto px-6 py-20 lg:py-28" data-testid="enquiry-section">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Request a Quote</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="enquiry-heading">
            Tell us what you need — we'll quote within 24 hours
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Prefer to talk? Call or WhatsApp us directly and we'll walk you through fabrics, pricing and timelines.
          </p>
          <div className="mt-8 space-y-4" data-testid="enquiry-contact-details">
            <a href={`tel:${contact.phone}`} data-testid="enquiry-call-link"
              className="flex items-center gap-4 bg-[#161B22] border border-white/10 rounded-2xl p-5 hover:border-[#FF5A1F]/40 transition-colors">
              <span className="h-11 w-11 rounded-xl bg-[#FF5A1F]/15 flex items-center justify-center"><Phone size={20} className="text-[#FF5A1F]" /></span>
              <span><span className="block text-xs text-slate-400 uppercase tracking-wider">Call us</span><span className="font-bold">{contact.phone}</span></span>
            </a>
            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer" data-testid="enquiry-whatsapp-link"
              className="flex items-center gap-4 bg-[#161B22] border border-white/10 rounded-2xl p-5 hover:border-[#25D366]/40 transition-colors">
              <span className="h-11 w-11 rounded-xl bg-[#25D366]/15 flex items-center justify-center"><MessageCircle size={20} className="text-[#25D366]" /></span>
              <span><span className="block text-xs text-slate-400 uppercase tracking-wider">WhatsApp</span><span className="font-bold">{contact.phone}</span></span>
            </a>
            <div className="flex items-center gap-4 bg-[#161B22] border border-white/10 rounded-2xl p-5">
              <span className="h-11 w-11 rounded-xl bg-[#2563EB]/15 flex items-center justify-center"><Mail size={20} className="text-[#2563EB]" /></span>
              <span><span className="block text-xs text-slate-400 uppercase tracking-wider">Email</span><span className="font-bold">{contact.email}</span></span>
            </div>
            <div className="flex items-center gap-4 bg-[#161B22] border border-white/10 rounded-2xl p-5">
              <span className="h-11 w-11 rounded-xl bg-[#10B981]/15 flex items-center justify-center"><MapPin size={20} className="text-[#10B981]" /></span>
              <span><span className="block text-xs text-slate-400 uppercase tracking-wider">Address</span><span className="font-bold">{contact.address}</span></span>
            </div>
            <div className="flex items-center gap-4 bg-[#161B22] border border-white/10 rounded-2xl p-5">
              <span className="h-11 w-11 rounded-xl bg-[#FF5A1F]/15 flex items-center justify-center"><Clock size={20} className="text-[#FF5A1F]" /></span>
              <span><span className="block text-xs text-slate-400 uppercase tracking-wider">Hours</span><span className="font-bold">{contact.hours}</span></span>
            </div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="b2b-enquiry-form"
          className="lg:col-span-3 bg-[#161B22] border border-white/10 rounded-3xl p-8 lg:p-10 self-start">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="enq-name" className="text-slate-300">Your Name *</Label>
              <Input id="enq-name" data-testid="enquiry-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. James Smith" className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <Label htmlFor="enq-company" className="text-slate-300">Company / Brand</Label>
              <Input id="enq-company" data-testid="enquiry-company-input" value={form.company} onChange={(e) => set("company", e.target.value)}
                placeholder="e.g. Smith Apparel Ltd" className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <Label htmlFor="enq-phone" className="text-slate-300">Phone *</Label>
              <Input id="enq-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                placeholder="+44 ..." className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <Label htmlFor="enq-email" className="text-slate-300">Email *</Label>
              <Input id="enq-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder="you@company.com" className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <Label className="text-slate-300">Product Interest</Label>
              <Select value={form.product_interest} onValueChange={(v) => set("product_interest", v)}>
                <SelectTrigger data-testid="enquiry-product-select" className="mt-2 bg-[#0D0F12] border-white/10 text-white">
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent className="bg-[#161B22] border-white/10 text-white">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`enquiry-product-option-${p.id}`}>{p.name}</SelectItem>
                  ))}
                  <SelectItem value="other" data-testid="enquiry-product-option-other">Other / Mixed Order</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Estimated Quantity</Label>
              <Select value={form.quantity} onValueChange={(v) => set("quantity", v)}>
                <SelectTrigger data-testid="enquiry-quantity-select" className="mt-2 bg-[#0D0F12] border-white/10 text-white">
                  <SelectValue placeholder="Select quantity" />
                </SelectTrigger>
                <SelectContent className="bg-[#161B22] border-white/10 text-white">
                  {QUANTITIES.map((q) => (
                    <SelectItem key={q} value={q} data-testid={`enquiry-quantity-option-${q}`}>{q}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5">
            <Label htmlFor="enq-message" className="text-slate-300">Tell us about your order</Label>
            <Textarea id="enq-message" data-testid="enquiry-message-input" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
              placeholder="Colours, sizes, artwork, deadline — anything that helps us quote accurately."
              className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
          </div>
          <div className="mt-5">
            <Label className="text-slate-300">Upload your design or artwork (optional)</Label>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
              data-testid="enquiry-artwork-upload-input" onChange={pickFile} />
            {attachment ? (
              <div className="mt-2 flex items-center justify-between gap-3 border border-[#10B981]/40 bg-[#10B981]/10 rounded-2xl px-5 py-4" data-testid="enquiry-artwork-attached">
                <span className="text-sm text-slate-200 truncate">{attachment.name}</span>
                <button type="button" onClick={() => setAttachment(null)} data-testid="enquiry-artwork-remove-button"
                  className="text-slate-400 hover:text-red-400 transition-colors" aria-label="Remove file">
                  <X size={17} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="enquiry-artwork-upload-button"
                className="mt-2 w-full border-2 border-dashed border-white/15 hover:border-[#FF5A1F]/50 rounded-2xl px-5 py-8 flex flex-col items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-60">
                <Upload size={22} className="text-[#FF5A1F]" />
                <span className="text-sm font-semibold">{uploading ? "Uploading..." : "Click to upload an image or PDF"}</span>
                <span className="text-xs text-slate-500">JPG, PNG, WEBP or PDF — max 10MB</span>
              </button>
            )}
          </div>
          <button type="submit" disabled={sending} data-testid="b2b-enquiry-submit-button"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#E04810] disabled:opacity-60 text-white font-bold rounded-full px-7 py-4 transition-colors">
            {sending ? "Sending..." : (<>Send Enquiry <Send size={17} /></>)}
          </button>
          <p className="mt-4 text-xs text-slate-500 text-center">
            Your details go straight to our sales team — no spam, ever.
          </p>
        </form>
      </div>
    </section>
  );
}
